import { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import QuestionCard from "@/components/QuestionCard";
import NoteDrawer from "@/components/NoteDrawer";
import ShareDialog from "@/components/ShareDialog";
import StudyText from "@/components/StudyText";
import { Highlight } from "@/components/QuestionCard";
import { useSearchReturn } from "@/components/search/BackToSearch";
import VerseWithWords from "@/components/VerseWithWords";
import BibleReader, { READER_KEY } from "@/components/BibleReader";
import LookCloser from "@/components/LookCloser";
import CommentaryPanel, { COMMENTARY_KEY, useCommentaries } from "@/components/commentary/CommentaryPanel";
import { CommentaryChooser } from "@/components/commentary/CommentaryChooser";
import { useSiteSettings } from "@/hooks/use-site-settings";
import { useHighlights } from "@/components/highlights/useHighlights";
import { BookMarked, X, ShieldCheck, Pencil } from "lucide-react";
import { ReportCorrection } from "@/components/ReportCorrection";
import { scrollToEl } from "@/components/ChapterNav";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useRefTagger } from "@/components/RefTagger";
import RelatedScriptures from "@/components/RelatedScriptures";
import WordStudy from "@/components/WordStudy";
import QuestionNav from "@/components/QuestionNav";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChevronLeft,
  ChevronRight,
  Bookmark,
  PenLine,
  MoveRight,
  Loader2,
  Lightbulb,
  MessageCircleQuestion,
  BookOpen,
  Link2,
  Languages,
  Share2,
  HandHeart,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const STUDY_ORDER = ["purpose", "response", "historical_context", "commentary", "commentaries", "look_closer", "your_turn", "application"];
const DEFAULT_LABELS = {
  purpose: { label: "Why It Was Asked", desc: "The purpose behind the question" },
  response: { label: "The Response", desc: "What Scripture records" },
  historical_context: { label: "Historical Context", desc: "The world behind the words" },
  commentary: { label: "The Bigger Picture", desc: "Where this fits in God's story" },
  commentaries: { label: "The Commentaries", desc: "Voices from across the centuries" },
  look_closer: { label: "Look Closer" },
  your_turn: { label: "Your Turn to Ask" },
  application: { label: "For Your Life Today" },
  keep_questioning: { label: "Keep questioning" },
  scene: { label: "The Scene" },
  related_scriptures: { label: "Related Scriptures" },
  word_study: { label: "Word Study" },
};

function shareDescription(q) {
  const text = (q.scene || q.purpose || q.historical_context || "").replace(/\s+/g, " ").trim() || `${q.reference} (KJV): ${q.verse_kjv}`;
  if (text.length <= 155) return text;
  return `${text.slice(0, 154).replace(/\s+\S*$/, "").replace(/[,;:.]+$/, "")}\u2026`;
}

function PeopleLinks({ names, role, testId }) {
  return (
    <p className="font-serif text-lg text-foreground" data-testid={testId}>
      {names.map((n, i) => (
        <span key={n}>
          {i > 0 && ", "}
          <Link to={`/explore/people/${encodeURIComponent(n)}?role=${role}`} className="transition-colors hover:text-primary hover:underline hover:decoration-1 hover:underline-offset-4" title={`Every question ${role === "asked" ? "asked by" : "asked to"} ${n}`} data-testid={`${testId}-link-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{n}</Link>
        </span>
      ))}
    </p>
  );
}

function AskLines({ text }) {
  const lines = text.split(/\n+/).map((l) => l.replace(/^[-•*]\s*/, "").trim()).filter(Boolean);
  if (lines.length <= 1) return <p className="mt-4 font-serif text-xl leading-[1.15] sm:text-2xl" data-testid="ask-each-other-text">{lines[0] || text}</p>;
  return (
    <ol className="mt-4 divide-y divide-[#D9C28E]/60" data-testid="ask-each-other-list">
      {lines.map((l, i) => (
        <li key={`${i}-${l.slice(0, 40)}`} className="flex gap-4 py-4 first:pt-0 last:pb-0" data-testid={`ask-each-other-item-${i}`}>
          <span className="mt-1.5 shrink-0 font-mono text-xs tracking-widest text-[#B8862B]" aria-hidden>{String(i + 1).padStart(2, "0")}</span>
          <span className="font-serif text-xl leading-[1.15] sm:text-2xl">{l}</span>
        </li>
      ))}
    </ol>
  );
}

function YourTurnToAsk({ askGod, askOthers, title }) {
  return (
    <div id="section-your-turn" className="qg-rise relative overflow-hidden rounded-2xl border border-[#D9C28E] p-6 text-[#2A2117] sm:p-10" style={{ background: "linear-gradient(160deg, #F8EFDA 0%, #F1E2C2 100%)" }} data-testid="ask-section">
      <span aria-hidden className="pointer-events-none absolute -right-4 -top-16 select-none font-serif text-[22rem] italic leading-none text-[#B8862B]/[0.09]">?</span>
      <div className="relative">
        <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-[#B8862B]">
          <span className="h-px w-8 bg-[#B8862B]" /> {title}
        </div>
        <h2 className="mt-2 font-serif text-xl font-semibold leading-[1.1] sm:text-2xl">Carry the question into prayer and conversation.</h2>

        {askGod && (
          <div className="mt-8" data-testid="ask-god">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#8A6A1F]">
              <HandHeart className="h-4 w-4" /> Ask God
            </div>
            <blockquote className="mt-3 border-l-2 border-[#B8862B] pl-5 font-serif text-2xl italic leading-[1.15] sm:pl-6 sm:text-3xl">{askGod}</blockquote>
            <p className="mt-3 pl-5 text-xs text-[#8A7350] sm:pl-6">A prayer to carry into the day.</p>
          </div>
        )}

        {askOthers && (
          <div className={askGod ? "mt-8 border-t border-[#D9C28E] pt-8" : "mt-8"} data-testid="ask-each-other">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#7A3B2E]">
              <Users className="h-4 w-4" /> Ask Yourself and Others
            </div>
            <AskLines text={askOthers} />
            <p className="mt-4 text-xs text-[#8A7350]">For you, a small group, a friend, or the dinner table.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function QuestionDetail({ preview = false }) {
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const hl = sp.get("hl") || "";
  const marks = hl ? (hl.match(/^"(.+)"$/) ? [hl.slice(1, -1)] : hl.split(/\s+/).filter((t) => t.length >= 2 && !/^\d+$/.test(t))) : [];
  const targetSec = sp.get("sec");
  const searchReturn = useSearchReturn();
  const clearMarks = () => setSp((prev) => { const n = new URLSearchParams(prev); n.delete("hl"); n.delete("sec"); return n; }, { replace: true });
  const { isFavorite, toggleFavorite, user, openAuth } = useAuth();
  const [version, setVersion] = useState("kjv");
  const [noteOpen, setNoteOpen] = useState(false);
  const location = useLocation();
  const [readerOpen, setReaderOpen] = useState(() => localStorage.getItem(READER_KEY) === "1" && window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    if (location.state?.openReader && window.matchMedia("(min-width: 1024px)").matches) setReaderOpen(true);
  }, [location.state]);
  const [readerChapter, setReaderChapter] = useState(null);
  const [sideTab, setSideTab] = useState("bible");
  const [commentarySlug, setCommentarySlug] = useState(() => localStorage.getItem(COMMENTARY_KEY) || "matthew-henry");
  const { data: commentaries } = useCommentaries();
  const hasCommentary = !!commentaries?.length;
  useEffect(() => { if (!hasCommentary && commentaries) setSideTab("bible"); }, [hasCommentary, commentaries]);
  useEffect(() => { if (window.matchMedia("(min-width: 1024px)").matches) localStorage.setItem(READER_KEY, readerOpen ? "1" : "0"); }, [readerOpen]);
  useEffect(() => { setReaderChapter(null); }, [id]);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const fn = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  const [shareOpen, setShareOpen] = useState(false);
  const [tab, setTab] = useState("study");
  const [focusWord, setFocusWord] = useState(null);

  useEffect(() => {
    if (!targetSec) return;
    const t = setTimeout(() => scrollToEl(document.getElementById(`section-${targetSec}`), -110), 350);
    return () => clearTimeout(t);
  }, [targetSec, id]);

  const { data: q, isLoading } = useQuery({
    queryKey: [preview ? "question-preview" : "question", id],
    queryFn: async () => (await api.get(preview ? `/admin/questions/${id}/preview` : `/questions/${id}`)).data,
  });

  useRefTagger([q?.id, tab, version, focusWord]);
  const { settings: siteSettings } = useSiteSettings();
  const hidden = new Set(q?.hidden_fields || []);
  const themesOn = siteSettings.explore_themes;
  const labels = { ...DEFAULT_LABELS, ...(q?.section_labels || {}) };
  const label = (k) => labels[k]?.label || k;
  const blocks = (q?.section_order || STUDY_ORDER).filter((k) => {
    if (hidden.has(k) || !q) return false;
    if (k === "look_closer" || k === "commentaries") return true;
    if (k === "your_turn") return !!(q.ask_god || q.ask_each_other);
    return k === "historical_context" ? !!q[k] : true;
  });

  const exploreWord = (name) => {
    setTab("words");
    setFocusWord(null);
    setTimeout(() => setFocusWord(name), 30);
  };

  const sideHl = useHighlights(q?.book, readerChapter ?? q?.chapter);

  if (isLoading || !q) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const saved = isFavorite(q.id);
  const question = version === "esv" ? q.question_esv : q.question_kjv;
  const verse = (version === "esv" ? q.verse_esv : q.verse_kjv) || question;

  const onSave = async () => {
    const r = await toggleFavorite(q.id);
    if (r?.needAuth) toast.info("Sign in to save questions.");
    else if (r?.added) toast.success("Saved to your study.");
  };

  const onNote = () => {
    if (!user) { openAuth(); return; }
    setNoteOpen(true);
  };

  const openCommentary = (slug) => {
    setCommentarySlug(slug);
    localStorage.setItem(COMMENTARY_KEY, slug);
    setReaderChapter(null);
    setSideTab("commentary");
    setReaderOpen(true);
  };

  const readerAvailable = true;
  const showReader = readerAvailable && readerOpen;
  const chapter = readerChapter ?? q.chapter;
  const reader = (
    <div className="flex h-full flex-col gap-2" data-testid="side-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex overflow-hidden rounded-full border border-border bg-background font-mono text-[10px] uppercase tracking-wider" data-testid="side-tabs">
          {[["bible", "Bible", BookOpen], ...(hasCommentary ? [["commentary", "Commentary", BookMarked]] : [])].map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setSideTab(k)} aria-pressed={sideTab === k} data-testid={`side-tab-${k}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${sideTab === k ? "bg-gold text-white" : "text-muted-foreground hover:text-foreground"}`}><Icon className="h-3 w-3" /> {l}</button>
          ))}
        </div>
        {sideTab === "commentary" && (
          <button type="button" onClick={() => setReaderOpen(false)} aria-label="Close panel" data-testid="side-close" className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-4 w-4" /></button>
        )}
      </div>
      <div className="min-h-0 flex-1">
        {sideTab === "bible" ? (
          <BibleReader book={q.book} chapter={chapter} verse={q.verse} verseChapter={q.chapter} activeId={q.id} onClose={() => setReaderOpen(false)} onChapter={setReaderChapter} />
        ) : (
          <CommentaryPanel slug={commentarySlug} onSlug={(v) => { setCommentarySlug(v); localStorage.setItem(COMMENTARY_KEY, v); }} book={q.book} chapter={chapter} verse={chapter === q.chapter ? q.verse : null}
            onJump={() => setSideTab("bible")} actions={sideHl} highlights={sideHl.all.filter((h) => h.source === "commentary" && h.commentary === commentarySlug)} />
        )}
      </div>
    </div>
  );

  return (
    <div className={`mx-auto px-4 py-8 sm:px-6 lg:px-8 ${showReader ? "max-w-[1600px] lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(380px,42%)] lg:gap-8" : "max-w-4xl"}`} data-testid="question-detail" data-reader-open={showReader ? "1" : "0"}>
    <div className={showReader ? "mx-auto w-full max-w-4xl min-w-0" : ""}>
      {preview && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-3" data-testid="preview-banner">
          <p className="text-sm text-foreground"><span className="font-mono text-[10px] uppercase tracking-wider text-gold">Admin preview · {q.status}</span><br />This is how the page will look once published. {q.status !== "published" && "It is not visible to visitors yet."}</p>
          <Link to={`/admin?tab=review&id=${q.id}`} className="rounded-full border border-gold px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-gold/20" data-testid="preview-back-to-review">Back to review</Link>
        </div>
      )}
      <Seo title={q.reference} description={shareDescription(q)} image={`${process.env.REACT_APP_BACKEND_URL}/api/questions/${q.id}/card.png`} type="article" />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Link
          to={searchReturn || "/explore"}
          data-testid={searchReturn ? "back-to-search" : "back-to-explore"}
          className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> {searchReturn ? "Back to search results" : "Back to database"}
        </Link>
        <div className="flex items-center gap-3">
        {readerAvailable && (
          <button
            type="button"
            onClick={() => setReaderOpen((o) => !o)}
            data-testid="toggle-reader-button"
            aria-pressed={showReader}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${showReader ? "border-gold bg-gold/15 text-foreground" : "border-border bg-card text-muted-foreground hover:border-gold hover:text-foreground"}`}
          >
            <BookOpen className="h-3.5 w-3.5 text-gold" /> {showReader ? "Hide chapter" : `Read ${q.book} ${q.chapter}`}
          </button>
        )}
        <div className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
          {q.prev ? (
            <Link to={`/question/${q.prev.id}`} data-testid="question-prev-top" title={q.prev.reference} className="flex h-8 w-8 items-center justify-center rounded-full border border-border transition-colors hover:border-gold hover:text-foreground">
              <ChevronLeft className="h-4 w-4" />
            </Link>
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border opacity-30"><ChevronLeft className="h-4 w-4" /></span>
          )}
          <span className="px-2" data-testid="question-position-top">{preview ? "Preview" : `${q.position} / ${q.total}`}</span>
          {q.next ? (
            <Link to={`/question/${q.next.id}`} data-testid="question-next-top" title={q.next.reference} className="flex h-8 w-8 items-center justify-center rounded-full border border-border transition-colors hover:border-gold hover:text-foreground">
              <ChevronRight className="h-4 w-4" />
            </Link>
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border opacity-30"><ChevronRight className="h-4 w-4" /></span>
          )}
        </div>
        </div>
      </div>

      {/* Verse card */}
      <div className="grain relative overflow-hidden rounded-3xl border border-border bg-card p-8 sm:p-12">
        <div className="relative z-10">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${
                q.testament === "OT" ? "bg-primary/12 text-primary" : "bg-secondary/15 text-secondary dark:text-sky-300"
              }`}
            >
              {q.testament === "OT" ? "Old Testament" : "New Testament"}
            </span>
            <span className="no-reftag font-mono text-sm font-medium text-muted-foreground">{q.reference}</span>
            {q.division && (
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">{q.division}</span>
            )}
            {user?.role === "admin" && (
              <Link to={`/admin?tab=questions&edit=${q.id}`} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid="question-admin-edit">
                <Pencil className="h-3 w-3" /> Edit in Admin
              </Link>
            )}
          </div>

          {marks.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-2 rounded-full border border-gold/50 bg-gold/10 py-1.5 pl-4 pr-1.5 text-sm text-foreground" data-testid="search-marks-pill">
              <span>Showing search matches for <strong>“{marks.join(" ")}”</strong></span>
              <button type="button" onClick={clearMarks} data-testid="search-marks-clear" className="rounded-full bg-card px-3 py-1 text-xs font-semibold text-foreground hover:bg-muted">Clear</button>
            </div>
          )}
          <blockquote className="mt-6 border-l-2 border-gold pl-5 font-serif text-base font-medium leading-snug text-muted-foreground sm:text-lg">
            &ldquo;<Highlight text={question} terms={marks} />&rdquo;
          </blockquote>

          <div className="no-reftag mt-6 pl-5" data-testid="full-verse">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              The full verse · {q.reference} · {version.toUpperCase()}
            </p>
            <div className="mt-2 font-serif text-2xl leading-relaxed text-foreground sm:text-3xl">
              <VerseWithWords text={verse} words={q.defined_words} tokens={version === "kjv" ? q.verse_tokens : []} onExplore={exploreWord} />
            </div>
          </div>

          <div className="mt-5 inline-flex overflow-hidden rounded-full border border-border">
            {["kjv", "esv"].map((v) => (
              <button
                key={v}
                data-testid={`version-toggle-${v}`}
                onClick={() => setVersion(v)}
                className={`px-4 py-1.5 text-xs font-semibold uppercase transition-colors ${
                  version === v ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-start" data-testid="asked-by-to">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Asked by</p>
              <PeopleLinks names={q.who_asked} role="asked" testId="asked-by" />
              {q.who_asked_detail && <p className="mt-1 text-xs italic leading-snug text-muted-foreground" data-testid="asked-by-detail">{q.who_asked_detail}</p>}
            </div>
            <MoveRight className="hidden h-4 w-4 text-gold sm:mt-5 sm:block" aria-hidden="true" />
            <div className="min-w-0 border-t border-border/60 pt-3 sm:border-0 sm:pt-0">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Asked to</p>
              <PeopleLinks names={q.asked_to} role="received" testId="asked-to" />
              {q.asked_to_detail && <p className="mt-1 text-xs italic leading-snug text-muted-foreground" data-testid="asked-to-detail">{q.asked_to_detail}</p>}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="question-tags">
              {q.type && !hidden.has("type_tag") && <Link
                to={`/type/${encodeURIComponent(q.type)}`}
                data-testid="detail-type-link"
                title={`All ${q.type} questions`}
                className="rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-gold transition-colors hover:bg-accent/30 hover:underline"
              >
                {q.type}
              </Link>}
              {!hidden.has("themes") && (q.themes?.length ? q.themes : [q.theme]).filter(Boolean).map((t) => (
                <Link key={t} to={themesOn ? `/explore/themes/${encodeURIComponent(t)}` : `/search?q=${encodeURIComponent(t)}`} className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted/70 hover:underline" data-testid={`theme-pill-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{t}</Link>
              ))}
          </div>

          {q.scene && (
            <div className="mt-5 max-w-2xl border-l-2 border-gold/60 pl-4" data-testid="question-scene">
              <p className="font-mono text-[10px] uppercase tracking-wider text-gold">{label("scene")}</p>
              <div className="mt-1 text-[15px] leading-relaxed text-foreground/90 [&_p]:text-[15px]">
                <StudyText text={q.scene} testId="question-scene-text" />
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="detail-save-button"
              onClick={onSave}
              variant={saved ? "default" : "outline"}
              className={`rounded-full ${saved ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}`}
            >
              <Bookmark className={`mr-2 h-4 w-4 ${saved ? "fill-current" : ""}`} />
              {saved ? "Saved" : "Save to study"}
            </Button>
            <Button data-testid="detail-note-button" onClick={onNote} variant="outline" className="rounded-full">
              <PenLine className="mr-2 h-4 w-4" /> Journal
            </Button>
            <Button data-testid="detail-share-button" onClick={() => setShareOpen(true)} variant="outline" className="rounded-full">
              <Share2 className="mr-2 h-4 w-4" /> Share
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <ReportCorrection reference={`${q.reference} — “${q.question_kjv}”`} testId="question-report-correction" />
            {user?.role === "admin" && (
              <Link to={`/admin?tab=review&id=${q.id}`} className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground" data-testid="question-admin-review">
                <ShieldCheck className="h-3 w-3 text-gold" /> Review (admin)
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Study tabs */}
      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="flex h-auto w-full gap-1 rounded-full bg-muted p-1 sm:w-auto sm:justify-start">
          <TabsTrigger value="study" data-testid="tab-study" className="flex-1 justify-center whitespace-nowrap rounded-full px-2.5 py-2 text-xs sm:flex-none sm:px-4 sm:text-sm">
            <BookOpen className="mr-1.5 h-4 w-4 shrink-0 sm:mr-2" /> Study
          </TabsTrigger>
          {!hidden.has("related_scriptures") && <TabsTrigger value="scriptures" data-testid="tab-scriptures" className="flex-1 justify-center whitespace-nowrap rounded-full px-2.5 py-2 text-xs sm:flex-none sm:px-4 sm:text-sm">
            <Link2 className="mr-1.5 h-4 w-4 shrink-0 sm:mr-2" />
            <span className="sm:hidden">Verses</span>
            <span className="hidden sm:inline">{label("related_scriptures")}</span>
            {q.related_scriptures?.length > 0 && (
              <span className="ml-1.5 rounded-full bg-gold/20 px-1.5 font-mono text-[10px] text-gold sm:ml-2">
                {q.related_scriptures.reduce((n, g) => n + g.refs.length, 0)}
              </span>
            )}
          </TabsTrigger>}
          {!hidden.has("word_study") && <TabsTrigger value="words" data-testid="tab-words" className="flex-1 justify-center whitespace-nowrap rounded-full px-2.5 py-2 text-xs sm:flex-none sm:px-4 sm:text-sm">
            <Languages className="mr-1.5 h-4 w-4 shrink-0 sm:mr-2" />
            <span className="sm:hidden">Words</span>
            <span className="hidden sm:inline">{label("word_study")}</span>
            {q.defined_words?.length > 0 && (
              <span className="ml-1.5 rounded-full bg-gold/20 px-1.5 font-mono text-[10px] text-gold sm:ml-2">{q.defined_words.filter((w) => !w.minor).length}</span>
            )}
          </TabsTrigger>}
        </TabsList>

        <TabsContent value="study" className="mt-6 space-y-4">
          {blocks.map((k, i) => {
            if (k === "look_closer") {
              const next = blocks[i + 1];
              return (
                <LookCloser key={k} questionId={q.id} title={label("look_closer")}
                  nextLabel={next ? label(next) : "Continue"}
                  onReadOn={() => {
                    const el = (next && document.getElementById(`section-${next === "your_turn" ? "your-turn" : next}`)) || document.querySelector('[data-testid="question-sources"]');
                    if (el) scrollToEl(el, -100);
                  }} />
              );
            }
            if (k === "commentaries") return (
              <CommentaryChooser key={k} q={q} title={label("commentaries")} desc={labels.commentaries?.desc}
                activeSlug={showReader && sideTab === "commentary" ? commentarySlug : null} onOpen={openCommentary} />
            );
            if (k === "your_turn") return <YourTurnToAsk key={k} askGod={q.ask_god} askOthers={q.ask_each_other} title={label("your_turn")} />;
            if (k === "application") return (
              <div key={k} style={{ animationDelay: `${i * 60}ms` }} id="section-application"
                className="qg-rise grain relative overflow-hidden rounded-2xl bg-secondary p-6 text-secondary-foreground sm:p-8" data-testid="section-application">
                <div className="relative z-10">
                  <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                    <Lightbulb className="h-4 w-4" /> {label("application")}
                  </div>
                  <div className="mt-4 [&_a]:text-gold [&_em]:text-white [&_p]:text-[15px] [&_p]:text-white/90">
                    <StudyText text={q.application} testId="section-text-application" />
                  </div>
                  <Button data-testid="prompt-reflect-button" onClick={onNote} className="mt-6 rounded-full bg-white text-secondary hover:bg-white/90">
                    <PenLine className="mr-2 h-4 w-4" /> Your Conversation with God
                  </Button>
                </div>
              </div>
            );
            return (
              <div key={k} style={{ animationDelay: `${i * 60}ms` }} id={`section-${k}`}
                className={`qg-rise rounded-2xl border bg-card p-6 sm:p-8 ${targetSec === k && marks.length ? "qg-target border-gold" : "border-border"}`} data-testid={`section-${k}`}>
                <div className="mb-4 flex items-baseline justify-between gap-3">
                  <h2 className="font-serif text-2xl font-semibold text-foreground">{label(k)}</h2>
                  {labels[k]?.desc && <span className="hidden font-mono text-[11px] uppercase tracking-wider text-muted-foreground sm:block">{labels[k].desc}</span>}
                </div>
                <StudyText text={q[k]} testId={`section-text-${k}`} marks={marks} />
              </div>
            );
          })}
          <p className="px-1 pt-2 text-center text-xs text-muted-foreground" data-testid="question-sources">
            Built from the biblical text and checked against trusted commentaries and lexicons.{" "}
            <Link to="/references" className="font-medium text-primary hover:underline" data-testid="sources-all-link">References &amp; Sources</Link>
          </p>
        </TabsContent>

        <TabsContent value="scriptures" className="mt-6">
          {q.related_scriptures?.length ? (
            <RelatedScriptures groups={q.related_scriptures} />
          ) : (
            <p className="text-sm text-muted-foreground">No related scriptures have been added yet.</p>
          )}
        </TabsContent>

        <TabsContent value="words" className="mt-6">
          {q.defined_words?.length ? (
            <WordStudy words={q.defined_words} focusName={focusWord} />
          ) : (
            <p className="text-sm text-muted-foreground">No word studies have been added for this verse yet.</p>
          )}
        </TabsContent>
      </Tabs>

      {/* Previous / next in Bible order */}
      {!preview && <QuestionNav prev={q.prev} next={q.next} position={q.position} total={q.total} className="mt-10" />}

      {/* Related */}
      {q.related?.length > 0 && (
        <div className="mt-14">
          <div className="mb-6 flex items-center gap-2">
            <MessageCircleQuestion className="h-5 w-5 text-gold" />
            <h2 className="font-serif text-2xl font-semibold text-foreground">{label("keep_questioning")}</h2>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {q.related.map((r, i) => (
              <QuestionCard key={r.id} q={r} index={i} />
            ))}
          </div>
        </div>
      )}

      <NoteDrawer open={noteOpen} onOpenChange={setNoteOpen} question={q} />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} question={q} />
    </div>
      {showReader && (
        <>
          <div className="hidden lg:block" data-testid="reader-column">
            <div className="sticky top-20 h-[calc(100vh-6rem)]">{reader}</div>
          </div>
          <Sheet open={showReader && isMobile} onOpenChange={(o) => !o && setReaderOpen(false)}>
            <SheetContent side="bottom" className="h-[88vh] rounded-t-3xl p-2 lg:hidden [&>button]:hidden" data-testid="reader-sheet">
              {reader}
            </SheetContent>
          </Sheet>
        </>
      )}
    </div>
  );
}
